import { useMentionCards } from "@/contexts/MentionCardsContext";
import { Separator } from "@components/ui/separator";
import { SidebarTrigger } from "@components/ui/sidebar";
import {
  Breadcrumb,
  BreadcrumbSeparator,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@components/ui/breadcrumb";
import { isBefore } from "date-fns";
import {
  DownloadIcon,
  InfoIcon,
  MailIcon,
  MoreHorizontalIcon,
  SendIcon,
  ShareIcon,
} from "lucide-react";
import { useRef, useState } from "react";
import {
  generatePath,
  Link,
  useHistory,
  useParams,
  useRouteMatch,
} from "react-router-dom";

import { hideIrrelevantMentions } from "@/api/endpoints/searches";
import { postTrackingData } from "@/api/endpoints/track";
import { URLS } from "@/api/urls";
import { Button } from "@/components/ui/button";
import { CALENDAR_START_DATE_DEFAULT } from "@/constants";
import Initializing from "@/containers/Initializing";
import { FEATURE } from "@/features";
import useFilterParams from "@/hooks/useFilterParams";
import { useHasFeature } from "@/hooks/useHasFeature";
import { useUser } from "@/hooks/useUser";
import { RELATIVE_PATHS } from "@/routes_new";
import { EVENT } from "@/tracking_events";
import { stringifyUrl } from "@/utils";
import QuickSubscribe from "@/views/MentionsView/partials/QuickSubscribe";
import AllEarsTitle from "@components/AllEarsTitle";
import Container from "@components/Container";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";

import { useOrganization } from "@/hooks/useOrganization";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@components/ui/alert";
import { i18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import FilterButton from "../FilterButton";
import AlertExportModal from "../partials/AlertExportModal";
import AlertShareModal from "../partials/AlertShareModal";
import DateRangeButton from "../partials/DateRangeButton";
import SortButton from "../partials/SortButton";
import TopWidgets from "../partials/TopWidgets";
import { MENTION_VIEW_DATE_RANGES } from "./ranges";
import { Tabs, TabsList, TabsTrigger } from "@components/ui/tabs";
import { Spinner } from "@components/ui/spinner";

export type MentionLayoutProps = { children: React.ReactNode; url: string };

const MentionLayout: React.FunctionComponent<MentionLayoutProps> = ({
  children,
  url,
}) => {
  const { t } = useLingui();
  const { orgUuid, uuid } = useParams<{ orgUuid: string; uuid: string }>();
  const { data: user } = useUser();
  const { id: user_id = "" } = user?.ok ? user.data : {};
  const { data: orgData } = useOrganization();
  const { plan } = orgData?.ok ? orgData.data : {};
  const allowedIntervals =
    plan?.notification_subscription_intervals || undefined;

  const { alert, meta } = useMentionCards();
  const [isSharing, setIsSharing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const isInsightsPage =
    useRouteMatch(`${url}${RELATIVE_PATHS.ALERT_INSIGHTS}/`)?.isExact || false;
  const history = useHistory();

  const [filters] = useFilterParams();
  const hasPptFeature = useHasFeature(FEATURE.HAS_PPT_FEATURE);

  const isPollInitiated = useRef(false);

  if (alert?.is_initializing && !isPollInitiated.current) {
    isPollInitiated.current = true;
    const checkIfSearchHasFinishedLoading = async () => {
      try {
        const response = await fetch(
          generatePath(URLS.SEARCHES_DETAIL, { uuid })
        );
        const data = await response.json();

        if (data?.is_initializing === false) {
          window.location.reload();
        } else {
          setTimeout(checkIfSearchHasFinishedLoading, 10000); // Poll with regular intervals
        }
      } catch {
        // Do nothing on error
      }
    };

    checkIfSearchHasFinishedLoading();
  }
  // Ranges for the datepicker
  const ranges = [
    {
      startDate: alert
        ? new Date(alert.start_date)
        : new Date(CALENDAR_START_DATE_DEFAULT),
      endDate: undefined,
      label: t`Alla träffar`,
    },
    ...MENTION_VIEW_DATE_RANGES.map(({ label, ...rest }) => ({
      label: i18n._(label),
      ...rest,
    })),
  ];

  const archived = alert
    ? alert.end_date && isBefore(new Date(alert.end_date), new Date())
    : false;

  return (
    <>
      <AllEarsTitle title={alert?.title} />
      {!alert ? (
        <div className="flex items-center justify-center h-full">
          <Spinner />
        </div>
      ) : !alert?.can_mentions_be_presented ? (
        <Initializing alert={alert} />
      ) : (
        <>
          <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
            <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6 max-w-350">
              <SidebarTrigger className="-ml-1" />

              <Separator
                orientation="vertical"
                className="mx-2 data-[orientation=vertical]:h-4"
              />

              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <Trans>Bevakningar</Trans>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>{alert?.title}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>

              <div className="flex items-center gap-2 ml-auto">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="md:hidden" size="icon" variant="ghost">
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent>
                    <DropdownMenuItem
                      onSelect={() => {
                        setIsSharing(true);
                      }}
                    >
                      <ShareIcon />
                      {isInsightsPage ? t`Dela analys` : t`Dela`}
                    </DropdownMenuItem>
                    {orgUuid && (
                      <DropdownMenuItem asChild>
                        <Link
                          to={generatePath(
                            `${url}${RELATIVE_PATHS.ALERT_SETTINGS}/`,
                            {
                              uuid,
                              orgUuid,
                            }
                          )}
                        >
                          <MailIcon />
                          {t`Prenumerera`}
                        </Link>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  data-testid="open-share-modal"
                  className="hidden md:flex"
                  onClick={() => {
                    postTrackingData(EVENT.SHARE_ALERT);
                    setIsSharing(true);
                  }}
                  variant="outline"
                  size="icon"
                >
                  <SendIcon />
                  <span className="sr-only">{t`Dela`}</span>
                </Button>

                <Button
                  data-testid="open-export-modal"
                  onClick={() => {
                    postTrackingData(EVENT.EXPORT_ALERT);
                    setIsExporting(true);
                  }}
                  variant="outline"
                >
                  {t`Exportera`}
                  <DownloadIcon />
                </Button>

                {!archived && user_id && orgUuid && (
                  <QuickSubscribe
                    className="hidden md:flex"
                    user_id={user_id}
                    organization_uuid={orgUuid}
                    search_uuid={uuid}
                    allowedIntervals={allowedIntervals}
                  />
                )}
              </div>
            </div>
          </header>

          <div className="border-b">
            <div className="w-full p-4 lg:p-6 max-w-350 flex justify-between items-end">
              <div className="flex flex-col gap-2.5">
                <h1
                  data-testid="mentions-title"
                  className="text-2xl font-bold w-full line-clamp-1"
                >
                  {alert?.title}
                </h1>

                {alert?.is_initializing == true ? (
                  <div className="flex items-center gap-2">
                    <Spinner />
                    <p className="text-muted-foreground">{t`Söker efter nya omnämnanden.`}</p>
                  </div>
                ) : null}
              </div>

              <div className="flex items-center gap-2.5">
                <DateRangeButton
                  key={alert.uuid}
                  ranges={ranges}
                  settings={{
                    min: alert?.start_date
                      ? new Date(alert.start_date)
                      : undefined,
                  }}
                />

                <FilterButton />
              </div>
            </div>
          </div>

          <TopWidgets
            isInsightsPage={isInsightsPage}
            alertUuid={uuid}
            filters={filters}
            url={url}
          />

          <Tabs
            value={isInsightsPage ? "insights" : "mentions"}
            defaultValue="mentions"
            onValueChange={() => {
              postTrackingData(EVENT.TOGGLE_ALERT_ANALYTICS, {
                to: isInsightsPage ? "alert" : "analytics",
              });

              isInsightsPage
                ? history.replace(
                    stringifyUrl({
                      url: generatePath(url, {
                        uuid: alert.uuid,
                        orgUuid,
                      }),
                      query: filters,
                    })
                  )
                : history.replace(
                    stringifyUrl({
                      url: generatePath(
                        `${url}${RELATIVE_PATHS.ALERT_INSIGHTS}/`,
                        {
                          orgUuid,
                          uuid: alert.uuid,
                        }
                      ),
                      query: filters,
                    })
                  );
            }}
          >
            <div className="relative">
              <TabsList className="px-6 w-full">
                <TabsTrigger value="mentions">
                  <Trans>Omnämnanden</Trans>
                </TabsTrigger>

                <TabsTrigger value="insights" data-testid="insights-toggle">
                  <Trans>Analys</Trans>
                </TabsTrigger>
              </TabsList>

              <div className="absolute z-0 inset-0 pointer-events-none max-w-350 flex justify-end px-6">
                <div className="pointer-events-auto">
                  <SortButton />
                </div>
              </div>
            </div>
          </Tabs>

          {meta?.llm_irrelevant_mentions_count ? (
            <Container>
              <Alert>
                <InfoIcon />
                <AlertTitle>
                  {meta?.llm_irrelevant_mentions_count}{" "}
                  <Trans>omnämnanden med låg relevans</Trans>
                </AlertTitle>
                <AlertDescription>
                  <Trans>Bedömt utifrån frågan</Trans>: &quot;
                  {alert?.information_need}
                  &quot;
                </AlertDescription>
                <AlertAction>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={async () => {
                      const res = await hideIrrelevantMentions(uuid);
                      if (res.ok) window.location.reload();
                    }}
                  >
                    <Trans>Dölj alla</Trans>
                  </Button>
                </AlertAction>
              </Alert>
            </Container>
          ) : null}

          <div className="pt-4">{children}</div>
        </>
      )}

      <AlertShareModal
        filters={filters}
        show={isSharing}
        onHide={() => {
          setIsSharing(false);
        }}
      />

      <AlertExportModal
        filters={filters}
        hasPptFeature={hasPptFeature}
        reportUuid={alert?.insights_report_uuid}
        show={isExporting}
        onHide={() => {
          setIsExporting(false);
        }}
      />
    </>
  );
};

export default MentionLayout;
